import { icons } from 'lucide-react';
import type { CSSProperties } from 'react';

/** Convert a kebab-case lucide name (e.g. "triangle-alert") to PascalCase ("TriangleAlert"). */
function toPascal(name: string): string {
  return name
    .split('-')
    .map((s) => (s ? s[0].toUpperCase() + s.slice(1) : ''))
    .join('');
}

/**
 * Legacy → current lucide names. lucide-react renamed a batch of icons
 * (the `*-circle` / `*-triangle` family flipped to `circle-*` / `triangle-*`),
 * and the old aliases are NOT present in the `icons` record, so they'd render
 * blank. Map the legacy names the app still uses to their current equivalents.
 */
const ALIASES: Record<string, string> = {
  'alert-triangle': 'triangle-alert',
  'check-circle': 'circle-check',
  'help-circle': 'circle-help',
  'file-signature': 'file-pen-line',
};

interface IconProps {
  name: string;
  size?: number | string;
  color?: string;
  strokeWidth?: number;
  style?: CSSProperties;
  className?: string;
}

/**
 * Drop-in replacement for the original `<i data-lucide="name">` glyphs.
 * Renders at 1em by default so it inherits the surrounding font-size, exactly
 * like the CSS rule `[data-lucide]{width:1em;height:1em}` did.
 */
export default function Icon({ name, size = '1em', color, strokeWidth = 1.5, style, className }: IconProps) {
  const Cmp = (icons as Record<string, React.ComponentType<any>>)[toPascal(ALIASES[name] ?? name)];
  if (!Cmp) return null;
  return <Cmp size={size} color={color} strokeWidth={strokeWidth} style={style} className={className} />;
}
